import { create } from 'zustand';

import type Stripe from 'stripe';

import { fetchProducts, fetchPrices } from '@/lib/services/paymentsDb';
import { getUserSubscription } from '@/lib/services/stripe';


export interface StripeSubscriptionResponse {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: number;
  items: {
    data: Array<{
      price: {
        id: string;
        unit_amount: number;
        currency: string;
      };
    }>;
  };
}

// Alias for backward compatibility - using union type to satisfy lint rules
export type StripeSubscriptionData = StripeSubscriptionResponse | null;

interface StripeProductsStoreState {
  // Data
  products: Stripe.Product[];
  prices: Stripe.Price[];
  subscription: StripeSubscriptionResponse | null;

  // Loading states
  isLoadingProducts: boolean;
  isLoadingPrices: boolean;
  isLoadingSubscription: boolean;

  // Error states
  productsError: string | null;
  pricesError: string | null;
  subscriptionError: string | null;

  // Cache timestamps
  productsLastFetchedAt: number | null;
  pricesLastFetchedAt: number | null;
  subscriptionLastFetchedAt: number | null;

  // Actions
  fetchStripeProducts: (forceRefresh?: boolean) => Promise<void>;
  fetchStripePrices: (forceRefresh?: boolean) => Promise<void>;
  fetchUserSubscription: (userId: string, forceRefresh?: boolean) => Promise<void>;
  getProductById: (productId: string) => Stripe.Product | null;
  getPriceById: (priceId: string) => Stripe.Price | null;
  getProductWithPrices: (productId: string) => { product: Stripe.Product; prices: Stripe.Price[] } | null;

  // Computed helpers (legacy — kept for backward compat)
  getBasicPlan: () => { product: Stripe.Product; prices: Stripe.Price[] } | null;
  getProPlan: () => { product: Stripe.Product; prices: Stripe.Price[] } | null;
  getPremiumPlan: () => { product: Stripe.Product; prices: Stripe.Price[] } | null;
  getCurrentPlan: () => { product: Stripe.Product | null; subscription: StripeSubscriptionResponse | null } | null;

  // New dynamic helpers — prefer these over legacy plan finders
  getStandardPlan: () => Stripe.Product | undefined;
  getPremiumEventPlan: () => Stripe.Product | undefined;
  getPlanByTier: (tier: string) => Stripe.Product | undefined;
  getPriceForProduct: (productId: string) => Stripe.Price | undefined;
  getActivePaidPlans: () => Stripe.Product[];
}

// Cache TTL: 5 minutes for products/prices (rarely change), 2 minutes for subscriptions
const PRODUCTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const PRICES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const SUBSCRIPTION_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

export const useStripeProductsStore = create<StripeProductsStoreState>((set, get) => ({
  // Initial state
  products: [],
  prices: [],
  subscription: null,
  isLoadingProducts: false,
  isLoadingPrices: false,
  isLoadingSubscription: false,
  productsError: null,
  pricesError: null,
  subscriptionError: null,
  productsLastFetchedAt: null,
  pricesLastFetchedAt: null,
  subscriptionLastFetchedAt: null,

  // Fetch products from edge function with caching
  fetchStripeProducts: async (forceRefresh = false) => {
    const state = get();
    const now = Date.now();

    // Skip if recently fetched and not forcing refresh
    if (
      !forceRefresh &&
      state.productsLastFetchedAt &&
      now - state.productsLastFetchedAt < PRODUCTS_CACHE_TTL &&
      state.products.length > 0
    ) {
      return;
    }

    // Prevent concurrent fetches
    if (state.isLoadingProducts) {
      return;
    }

    set({ isLoadingProducts: true, productsError: null });

    try {
      const result = await fetchProducts();

      if (result.error) {
        set({
          productsError: result.error,
          isLoadingProducts: false
        });
        return;
      }

      set({
        products: result.products,
        isLoadingProducts: false,
        productsError: null,
        productsLastFetchedAt: Date.now()
      });
    } catch (error) {
      set({
        productsError: error instanceof Error ? error.message : 'Failed to fetch products',
        isLoadingProducts: false
      });
    }
  },

  // Fetch prices from edge function with caching
  fetchStripePrices: async (forceRefresh = false) => {
    const state = get();
    const now = Date.now();

    // Skip if recently fetched and not forcing refresh
    if (
      !forceRefresh &&
      state.pricesLastFetchedAt &&
      now - state.pricesLastFetchedAt < PRICES_CACHE_TTL &&
      state.prices.length > 0
    ) {
      return;
    }

    // Prevent concurrent fetches
    if (state.isLoadingPrices) {
      return;
    }

    set({ isLoadingPrices: true, pricesError: null });

    try {
      const result = await fetchPrices();

      if (result.error) {
        set({
          pricesError: result.error,
          isLoadingPrices: false
        });
        return;
      }

      set({
        prices: result.prices,
        isLoadingPrices: false,
        pricesError: null,
        pricesLastFetchedAt: Date.now()
      });
    } catch (error) {
      set({
        pricesError: error instanceof Error ? error.message : 'Failed to fetch prices',
        isLoadingPrices: false
      });
    }
  },

  // Fetch user subscription from edge function with caching
  fetchUserSubscription: async (userId: string, forceRefresh = false) => {
    const state = get();
    const now = Date.now();

    // Skip if recently fetched and not forcing refresh
    if (
      !forceRefresh &&
      state.subscriptionLastFetchedAt &&
      now - state.subscriptionLastFetchedAt < SUBSCRIPTION_CACHE_TTL &&
      state.subscription !== null
    ) {
      return;
    }

    // Prevent concurrent fetches
    if (state.isLoadingSubscription) {
      return;
    }

    set({ isLoadingSubscription: true, subscriptionError: null });

    try {
      const result = await getUserSubscription(userId);

      set({
        subscription: result.subscription,
        isLoadingSubscription: false,
        subscriptionError: result.subscription ? null : 'No active subscription found',
        subscriptionLastFetchedAt: Date.now()
      });
    } catch (error) {
      set({
        subscriptionError: error instanceof Error ? error.message : 'Failed to fetch subscription',
        isLoadingSubscription: false
      });
    }
  },

  // Get product by ID
  getProductById: (productId: string) => {
    const { products } = get();
    return products.find(product => product.id === productId) || null;
  },

  // Get price by ID
  getPriceById: (priceId: string) => {
    const { prices } = get();
    return prices.find(price => price.id === priceId) || null;
  },

  // Get product with associated prices
  getProductWithPrices: (productId: string) => {
    const { products, prices } = get();
    const product = products.find(p => p.id === productId);
    
    if (!product) return null;
    
    const productPrices = prices.filter(price => price.product === productId);
    
    return { product, prices: productPrices };
  },

  // Get Basic Plan (lowest tier)
  getBasicPlan: () => {
    const { products, prices } = get();
    
    // Look for basic/starter product by metadata or name
    const basicProduct = products.find(product => 
      product.metadata?.tier_level === 'free' ||
      product.metadata?.tier === 'basic' ||
      product.name.toLowerCase().includes('basic') ||
      product.name.toLowerCase().includes('starter') ||
      product.name.toLowerCase().includes('free')
    );
    
    if (!basicProduct) return null;
    
    const productPrices = prices.filter(price => price.product === basicProduct.id);
    return { product: basicProduct, prices: productPrices };
  },

  // Get Pro Plan (middle tier)
  getProPlan: () => {
    const { products, prices } = get();
    
    // Look for pro/professional product
    const proProduct = products.find(product => 
      product.metadata?.tier_level === 'pro' ||
      product.metadata?.tier === 'pro' ||
      product.metadata?.tier === 'professional' ||
      product.name.toLowerCase().includes('pro') ||
      product.name.toLowerCase().includes('professional')
    );
    
    if (!proProduct) return null;
    
    const productPrices = prices.filter(price => price.product === proProduct.id);
    return { product: proProduct, prices: productPrices };
  },

  // Get Premium Plan (highest tier)
  getPremiumPlan: () => {
    const { products, prices } = get();
    
    // Look for premium/enterprise product
    const premiumProduct = products.find(product => 
      product.metadata?.tier_level === 'premium' ||
      product.metadata?.tier === 'premium' ||
      product.metadata?.tier === 'enterprise' ||
      product.metadata?.tier === 'team' ||
      product.name.toLowerCase().includes('premium') ||
      product.name.toLowerCase().includes('enterprise') ||
      product.name.toLowerCase().includes('team')
    );
    
    if (!premiumProduct) return null;
    
    const productPrices = prices.filter(price => price.product === premiumProduct.id);
    return { product: premiumProduct, prices: productPrices };
  },

  // Get current plan based on subscription
  getCurrentPlan: () => {
    const { subscription, prices, products } = get();

    if (!subscription?.items.data.length) {
      return null;
    }

    const currentPriceId = subscription.items.data[0].price.id;
    const currentPrice = prices.find(price => price.id === currentPriceId);

    if (!currentPrice) return null;

    const currentProduct = products.find(product => product.id === currentPrice.product);

    return {
      product: currentProduct || null,
      subscription
    };
  },

  // --- New dynamic helpers ---

  getStandardPlan: () => get().products.find(p => p.metadata?.tier_level === 'standard'),

  getPremiumEventPlan: () => get().products.find(p => p.metadata?.tier_level === 'premium_event'),

  getPlanByTier: (tier: string) => get().products.find(p => p.metadata?.tier_level === tier),

  getPriceForProduct: (productId: string) => get().prices.find(p => p.product === productId),

  getActivePaidPlans: () => {
    const products = get().products.filter(p =>
      p.active && p.metadata?.tier_level && p.metadata.tier_level !== 'free' && p.metadata.tier_level !== 'none'
    );
    return products.sort((a, b) => {
      const priceA = get().prices.find(p => p.product === a.id)?.unit_amount ?? 0;
      const priceB = get().prices.find(p => p.product === b.id)?.unit_amount ?? 0;
      return priceA - priceB;
    });
  },
})); 